import { Component, Suspense, lazy, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CSpinner } from "@coreui/react-pro";
import { ExpandableChart, type ChartPayload } from "@scryerlabs/ui/charts";

/**
 * Loaded on demand, so a member who never opens a candle chart never downloads the charting
 * library. It is a separate entry point and a separate chunk for exactly this.
 */
const AdvancedCandleChart = lazy(() =>
  import("@scryerlabs/ui/charts/advanced").then((module) => ({
    default: module.AdvancedCandleChart,
  })),
);

/**
 * A candle chart, with the two ways loading it can go wrong handled.
 *
 * Suspense covers the wait. The boundary covers the chunk failing outright — a dynamic import
 * rejects rather than suspending, so without it a flaky connection takes down the whole strategy
 * page rather than one tab.
 *
 * There is no simple renderer to fall back to. Chart.js cannot draw a real backtest at all, so the
 * fallback is saying so and offering a retry, not a worse chart.
 */
export function CandleChartPanel({
  payload,
  title,
  height = 340,
}: {
  payload: ChartPayload;
  title: string;
  height?: number;
}) {
  const { t } = useTranslation("tradingStrategies");

  return (
    <ChunkBoundary fallback={<Unavailable height={height} />}>
      <Suspense fallback={<Loading height={height} />}>
        {/* Inside the boundary, so a chart that failed to load offers nothing to expand. */}
        <ExpandableChart
          title={title}
          height={height}
          expandLabel={t("charts.expand")}
          closeLabel={t("charts.close")}
        >
          {(chartHeight) => (
            <AdvancedCandleChart
              payload={payload}
              height={chartHeight}
              resetLabel={t("charts.resetView")}
              tradesLabel={t("charts.trades")}
            />
          )}
        </ExpandableChart>
      </Suspense>
    </ChunkBoundary>
  );
}

function Loading({ height }: { height: number }) {
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ height }}
      role="status"
      aria-live="polite"
    >
      <CSpinner size="sm" />
    </div>
  );
}

function Unavailable({ height }: { height: number }) {
  const { t } = useTranslation("tradingStrategies");

  return (
    <div
      className="d-flex align-items-center justify-content-center text-center"
      style={{ height }}
    >
      <div className="text-body-secondary small">
        {t("charts.candlesUnavailable")}
        <br />
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => window.location.reload()}
        >
          {t("charts.retry")}
        </button>
      </div>
    </div>
  );
}

/**
 * Catches a failed chunk load and nothing else.
 *
 * A class because that is still the only way to catch a render error in React. Deliberately narrow:
 * it wraps one lazy import, so anything it catches is that import failing.
 */
class ChunkBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
